import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { CheckCircle2, CloudUpload, ExternalLink, Loader2, PlugZap, RefreshCw, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminHeader } from "./AdminLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PublishPanel } from "@/components/twentyi/PublishPanel";
import { DomainsPanel } from "@/components/twentyi/DomainsPanel";
import { DnsPanel } from "@/components/twentyi/DnsPanel";
import { MailPanel } from "@/components/twentyi/MailPanel";
import { AssignPanel } from "@/components/twentyi/AssignPanel";
import { ActivityPanel } from "@/components/twentyi/ActivityPanel";
import { PricesPanel } from "@/components/twentyi/PricesPanel";

const card = "card-tech rounded-2xl p-6";
const btn = "inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-60";
const select = "w-full rounded-lg border border-white/10 bg-[#07090E] px-3 py-2 text-sm text-white outline-none focus:border-cyan-400/60";
const when = (iso) => (iso ? format(parseISO(iso), "MMM d, yyyy HH:mm") : "never");
const detail = (e) => e.response?.data?.detail;

const Problem = ({ d, testId }) => {
  if (!d) return null;
  const obj = typeof d === "object" && !Array.isArray(d) ? d : { message: formatApiErrorDetail(d) };
  return (
    <div data-testid={testId} className="mt-4 rounded-xl border border-red-400/30 bg-red-500/10 p-4 text-sm text-red-200">
      <p className="flex items-center gap-2 font-semibold"><XCircle className="h-4 w-4" />{obj.message}</p>
      {obj.twentyi_error && <p className="mt-1 text-red-300/90">20i said: {obj.twentyi_error}</p>}
      {obj.outbound_ip && <p className="mt-1">Our server's outbound IP: <code data-testid={`${testId}-ip`} className="rounded bg-black/30 px-1.5 py-0.5">{obj.outbound_ip}</code></p>}
      {obj.steps?.length > 0 && <ol className="mt-2 list-decimal space-y-1 pl-5 text-red-100/90">{obj.steps.map((s) => <li key={s}>{s}</li>)}</ol>}
    </div>
  );
};

const ConnectionCard = ({ status, reload }) => {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [fail, setFail] = useState(null);
  const [usage, setUsage] = useState(null);
  useEffect(() => { if (status?.configured) api.get("/admin/twentyi/usage").then((r) => setUsage(r.data)).catch(() => {}); }, [status?.configured]);
  const test = async () => {
    setBusy(true); setFail(null); setRes(null);
    try { const r = await api.post("/admin/twentyi/test"); setRes(r.data); reload(); } catch (e) { setFail(detail(e)); }
    setBusy(false);
  };
  return (
    <div className={card}>
      <h2 className="font-display text-lg font-bold text-white">Connection</h2>
      <p className="mt-1 text-sm text-slate-400">Uses the 20i General API key stored on the server. The key never reaches this page.</p>
      <button onClick={test} disabled={busy || !status?.configured} data-testid="twentyi-test-btn" className={`${btn} mt-4 bg-cyan-400 text-[#07090E] hover:bg-cyan-300`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />} Test connection
      </button>
      {!status?.configured && <p data-testid="twentyi-not-configured" className="mt-3 text-sm text-amber-300">TWENTYI_API_KEY isn't set on the server.</p>}
      {res && (
        <div data-testid="twentyi-test-result" className="mt-4 rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-4 text-sm text-emerald-100">
          <p className="flex items-center gap-2 font-semibold"><CheckCircle2 className="h-4 w-4" />{res.message}</p>
          <p className="mt-1">Account: <span data-testid="twentyi-account-name">{res.account.name}</span> · {res.account.spec}</p>
        </div>
      )}
      <Problem d={fail} testId="twentyi-test-error" />
      <p className="mt-4 text-xs text-slate-500">Last test: {when(status?.last_test?.at)}{status?.last_test && !status.last_test.ok ? " (failed)" : ""} · Server IP: <span data-testid="twentyi-outbound-ip">{status?.outbound_ip || "unknown"}</span></p>
      <p className="mt-1 text-xs text-slate-500">Hosting plan: <span data-testid="twentyi-overview-usage" className="font-semibold text-white">{usage?.label || "…"}</span></p>
    </div>
  );
};

const SyncCard = ({ status, reload }) => {
  const [busy, setBusy] = useState(false);
  const [fail, setFail] = useState(null);
  const last = status?.last_sync;
  const sync = async () => {
    setBusy(true); setFail(null);
    try { const r = await api.post("/admin/twentyi/sync"); toast.success(`Synced ${r.data.domains} domains and ${r.data.packages} packages`); reload(); }
    catch (e) {
      setFail(detail(e));
      const wait = /(\d+)s/.exec(formatApiErrorDetail(detail(e)) || "");
      if (e.response?.status === 429) setTimeout(() => setFail(null), ((wait ? Number(wait[1]) : 60) + 1) * 1000);
    }
    setBusy(false);
  };
  return (
    <div className={card}>
      <h2 className="font-display text-lg font-bold text-white">Website Tracker sync</h2>
      <p className="mt-1 text-sm text-slate-400">Imports 20i domains (expiry dates) and hosting packages. Matches by domain, so revenue and notes you've entered are kept. Read-only toward 20i.</p>
      <button onClick={sync} disabled={busy || !status?.configured} data-testid="twentyi-sync-btn" className={`${btn} mt-4 border border-cyan-400/50 text-cyan-200 hover:bg-cyan-400/10`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sync from 20i
      </button>
      <Problem d={fail} testId="twentyi-sync-error" />
      {last && (
        <dl data-testid="twentyi-sync-result" className="mt-4 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
          {[["Domains", last.domains, "domains"], ["Packages", last.packages, "packages"], ["New entries", last.created, "created"], ["Updated", last.updated, "updated"]].map(([l, v, k]) => (
            <div key={k} className="rounded-xl border border-white/5 bg-white/[0.02] p-3"><dt className="text-xs text-slate-500">{l}</dt><dd data-testid={`twentyi-sync-${k}`} className="font-display text-xl font-bold text-white">{v}</dd></div>
          ))}
        </dl>
      )}
      <p className="mt-3 text-xs text-slate-500">Last sync: <span data-testid="twentyi-last-sync">{when(last?.at)}</span></p>
    </div>
  );
};

const UploadCard = () => {
  const [pkgs, setPkgs] = useState(null);
  const [projects, setProjects] = useState([]);
  const [versions, setVersions] = useState([]);
  const [f, setF] = useState({ package_id: "", project_id: "", version: "" });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [fail, setFail] = useState(null);
  useEffect(() => {
    api.get("/admin/twentyi/packages").then((r) => setPkgs(r.data)).catch((e) => { setPkgs([]); setFail(detail(e)); });
    api.get("/admin/studio/projects").then((r) => setProjects(r.data.filter((p) => p.version_count > 0 || p.status === "Built"))).catch(() => {});
  }, []);
  useEffect(() => {
    if (!f.project_id) return setVersions([]);
    api.get(`/admin/studio/projects/${f.project_id}`).then((r) => {
      const v = r.data.versions || [];
      setVersions(v); setF((x) => ({ ...x, version: v.length ? String(v[v.length - 1].number) : "" }));
    }).catch(() => setVersions([]));
  }, [f.project_id]);
  const run = async (overwrite_ok) => {
    setBusy(true); setFail(null); setRes(null);
    try {
      const r = await api.post("/admin/twentyi/upload", { package_id: Number(f.package_id), project_id: f.project_id, version: Number(f.version), overwrite_ok });
      setRes(r.data);
    } catch (e) { setFail(detail(e)); }
    setBusy(false);
  };
  const ready = f.package_id && f.project_id && f.version;
  return (
    <div className={`${card} lg:col-span-2`}>
      <h2 className="font-display text-lg font-bold text-white">Upload to 20i (test)</h2>
      <p className="mt-1 text-sm text-slate-400">Uploads a Studio version's files (same as the zip, pointed at our public API) into the package's public_html. Nothing on 20i is deleted; only files with the same name are replaced, and you're warned first if public_html isn't empty.</p>
      <div className="mt-4 grid gap-3 md:grid-cols-[2fr_2fr_1fr]">
        <label className="text-xs text-slate-400">20i package
          <select value={f.package_id} onChange={(e) => setF({ ...f, package_id: e.target.value })} data-testid="twentyi-package-select" className={select}>
            <option value="">{pkgs === null ? "Loading…" : pkgs.length ? "Choose a package" : "No packages"}</option>
            {(pkgs || []).map((p) => <option key={p.id} value={p.id} disabled={p.protected} data-testid={`twentyi-package-option-${p.id}`}>{p.name} · {p.type}{p.protected ? " — Protected live site" : ""}</option>)}
          </select>
        </label>
        <label className="text-xs text-slate-400">AI Studio project
          <select value={f.project_id} onChange={(e) => setF({ ...f, project_id: e.target.value, version: "" })} data-testid="twentyi-project-select" className={select}>
            <option value="">Choose a project</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label className="text-xs text-slate-400">Version
          <select value={f.version} onChange={(e) => setF({ ...f, version: e.target.value })} data-testid="twentyi-version-select" className={select}>
            {!versions.length && <option value="">—</option>}
            {versions.map((v) => <option key={v.number} value={v.number}>v{v.number}</option>)}
          </select>
        </label>
      </div>
      <button onClick={() => run(false)} disabled={busy || !ready} data-testid="twentyi-upload-btn" className={`${btn} mt-4 bg-cyan-400 text-[#07090E] hover:bg-cyan-300`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CloudUpload className="h-4 w-4" />} Upload to 20i (test)
      </button>
      {res?.status === "needs_confirm" && (
        <div data-testid="twentyi-upload-confirm" className="mt-4 rounded-xl border border-amber-400/30 bg-amber-500/10 p-4 text-sm text-amber-100">
          <p className="font-semibold">public_html already has {res.existing_count} item(s): {res.existing.join(", ")}</p>
          <p className="mt-1">{res.would_overwrite.length ? `These will be replaced: ${res.would_overwrite.join(", ")}.` : "No existing file will be replaced."} Nothing will be deleted.</p>
          <button onClick={() => run(true)} disabled={busy} data-testid="twentyi-upload-confirm-btn" className={`${btn} mt-3 bg-amber-400 text-[#07090E] hover:bg-amber-300`}>Upload anyway</button>
        </div>
      )}
      {res?.status === "uploaded" && (
        <div data-testid="twentyi-upload-result" className="mt-4 rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-4 text-sm text-emerald-100">
          <p className="flex items-center gap-2 font-semibold"><CheckCircle2 className="h-4 w-4" />{res.message} · {res.uploaded} files</p>
          {res.unlock && <p data-testid="twentyi-upload-unlock" className="mt-1 text-emerald-200/80">FTP unlocked {res.unlock.method === "ip" ? `for our IP ${res.unlock.ip}` : `until ${res.unlock.until}`}</p>}
          <div className="mt-2 flex flex-wrap gap-3">
            {res.temporary_url && <a href={res.temporary_url} target="_blank" rel="noopener noreferrer" data-testid="twentyi-temp-url-link" className="inline-flex items-center gap-1 underline">View site (20i temporary URL) <ExternalLink className="h-3.5 w-3.5" /></a>}
            {res.site_url && <a href={res.site_url} target="_blank" rel="noopener noreferrer" data-testid="twentyi-view-site-link" className="inline-flex items-center gap-1 underline opacity-80">Domain (works once DNS points to 20i) <ExternalLink className="h-3.5 w-3.5" /></a>}
          </div>
        </div>
      )}
      <Problem d={fail} testId="twentyi-upload-error" />
    </div>
  );
};

const PublishCard = () => {
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("");
  const [versions, setVersions] = useState([]);
  useEffect(() => { api.get("/admin/studio/projects").then((r) => setProjects(r.data.filter((p) => p.version_count > 0 || p.status === "Built"))).catch(() => {}); }, []);
  useEffect(() => { if (pid) api.get(`/admin/studio/projects/${pid}`).then((r) => setVersions(r.data.versions || [])).catch(() => setVersions([])); }, [pid]);
  return (
    <div className={card}>
      <select value={pid} onChange={(e) => { setVersions([]); setPid(e.target.value); }} data-testid="publish-project-select" className={`${select} mb-4`}>
        <option value="">Choose an AI Studio project</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      {pid && <PublishPanel projectId={pid} versions={versions} />}
    </div>
  );
};

const TABS = [["overview", "Overview"], ["publish", "Publish"], ["domains", "Domains"], ["prices", "Prices & fees"], ["dns", "DNS"], ["mail", "Mailboxes"], ["clients", "Client access"], ["activity", "Activity log"]];

export default function TwentyI() {
  const [status, setStatus] = useState(null);
  const [pkgs, setPkgs] = useState(null);
  const reload = () => api.get("/admin/twentyi/status").then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false }));
  useEffect(() => { reload(); api.get("/admin/twentyi/packages").then((r) => setPkgs(r.data)).catch(() => setPkgs([])); }, []);
  const wrap = (el) => <div className={card}>{el}</div>;
  return (
    <div data-testid="admin-twentyi-page">
      <AdminHeader eyebrow="// settings" title="20i hosting">Publish AI Studio sites, register domains, manage DNS and mailboxes on the 20i reseller account. The protected live site is never changed.</AdminHeader>
      <Tabs defaultValue="overview">
        <TabsList className="mb-5 flex h-auto flex-wrap bg-white/[0.03]">
          {TABS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`twentyi-tab-${v}`}>{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="overview">
          <div className="grid gap-5 lg:grid-cols-2">
            <ConnectionCard status={status} reload={reload} />
            <SyncCard status={status} reload={reload} />
            <UploadCard />
          </div>
        </TabsContent>
        <TabsContent value="publish"><PublishCard /></TabsContent>
        <TabsContent value="domains">{wrap(<DomainsPanel />)}</TabsContent>
        <TabsContent value="prices">{wrap(<PricesPanel />)}</TabsContent>
        <TabsContent value="dns">{wrap(<DnsPanel pkgs={pkgs} />)}</TabsContent>
        <TabsContent value="mail">{wrap(<MailPanel pkgs={pkgs} />)}</TabsContent>
        <TabsContent value="clients">{wrap(<AssignPanel pkgs={pkgs} />)}</TabsContent>
        <TabsContent value="activity">{wrap(<ActivityPanel />)}</TabsContent>
      </Tabs>
    </div>
  );
}
